/**
 * useSessionRestoration — IN_PROGRESS session restoration hook.
 *
 * Extracted from WorkflowMachineContext.jsx during Story 4.6 wrap-up.
 * Returns a memoized `restoreInProgressSession(session)` function that:
 *   1. Clears existing session data
 *   2. Hydrates the backend service refs (threadId/sessionId/etc.)
 *   3. Reconciles completed-agents / agent-awaiting-review state from the
 *      workflow status API
 *   4. Replays each completed agent's data into the slice contexts via
 *      the bridge dispatch (one switch arm per agent — 10 total)
 *   5. Restores Step 9 (Final Report) and Step 10 (Residual TARA)
 *      navigation state when applicable
 *   6. Sets the workflow machine state to REVIEWING or PROCESSING
 *
 * All slice writes go through the bridge `dispatch` — the residue routes
 * each action.type to the correct slice's action-creator.
 *
 * Body lifted verbatim from WorkflowMachineContext.jsx (Story 4.6); no
 * logic changes beyond closing over hook args instead of provider locals.
 */
import { useCallback } from 'react';
import logger from '../../../utils/logger';
import { ACTIONS } from '../contexts/WorkflowMachineContext';
import { WORKFLOW_STATES } from '../../../config/athenaWorkflowConfig';
import {
  createRestorationData,
  determineCurrentStep,
} from '../../../utils/sessionUtils';
import {
  extractStep2Data,
  extractStep3Data,
  extractStep4Data,
  extractStep5Data,
  extractStep6Data,
  extractStep7Data,
  extractStep8Data,
} from '../utils/stepDataExtractors';
import { getStepForAgent, getStepDataKey } from '../utils/stepMapping';
// Story 6.1: stateless api/ mirrors replacing AthenaBackendService methods.
import { getWorkflowStatus, getAgentResult } from '../../../api/workflow';
import { getResidualProgress } from '../../../api/residualTara';
import { getThreatChain } from '../../../api/impactAnalysis';

export function useSessionRestoration({
  backendServiceRef,
  dispatch,
  workflowSession,
  workflowMessages,
  workflowResults,
  stepNavigation,
  showReviewPanel,
}) {
  // Restore an IN_PROGRESS session
  const restoreInProgressSession = useCallback(async (session) => {
    logger.debug('🚀 RESTORATION FUNCTION CALLED - Enhanced restoration starting:', session.session_id);
    logger.debug('🔍 RESTORATION DEBUG - Session completed_agents:', session.completed_agents);
    logger.debug('🔍 RESTORATION DEBUG - Session current_agent:', session.current_agent);

    // IMPORTANT: Clear all existing session data before restoration
    // This prevents data from the previous session from bleeding into the restored session
    logger.debug('🧹 CLEARING existing session data before restoration...');
    dispatch({ type: ACTIONS.CLEAR_SESSION_FOR_RESTORATION });

    // Create initial restoration data
    const restorationData = createRestorationData(session);
    logger.debug('🔍 RESTORATION DEBUG - restorationData (initial):', restorationData);

    if (!restorationData) {
      logger.error('❌ Failed to create restoration data - session may be corrupted');
      // Return null to signal failure (not undefined from implicit return)
      return null;
    }

    // Set backend service context first so we can call status API
    backendServiceRef.current.threadId = restorationData.threadId;
    backendServiceRef.current.sessionId = restorationData.sessionId;
    backendServiceRef.current.tenantId = restorationData.tenantId;
    backendServiceRef.current.fileId = restorationData.fileId;

    // Get the actual workflow status from the status API
    logger.debug('🔍 RESTORATION: Getting workflow status from API...');
    let workflowStatus = null;
    try {
      workflowStatus = await getWorkflowStatus(backendServiceRef.current.threadId);
      logger.debug('✅ RESTORATION: Workflow status from API:', workflowStatus);
    } catch (error) {
      logger.error('❌ RESTORATION: Failed to get workflow status:', error);
    }

    // Override completed agents with data from workflow status API if available
    logger.debug('🔍 RESTORATION: Checking API override fields:', {
      hasWorkflowStatus: !!workflowStatus,
      hasAgentsCompleted: !!(workflowStatus && workflowStatus.agents_completed),
      agentsCompleted: workflowStatus?.agents_completed,
      currentAgent: workflowStatus?.current_agent
    });

    if (workflowStatus && workflowStatus.agents_completed) {
      logger.debug('🔄 RESTORATION: Overriding with API data...');
      restorationData.completedAgents = workflowStatus.agents_completed;
      restorationData.currentAgent = workflowStatus.current_agent;

      // Find the agent awaiting review using the new review_status field
      // agent_details contains AgentProgress with review_status: "pending" | "awaiting_review" | "approved"
      const agentDetails = workflowStatus.agent_details || {};
      let agentAwaitingReview = null;

      // Check ALL agents in agent_details for review_status === "awaiting_review"
      // An agent can be completed (status: 'completed') but not yet in agents_completed
      // because agents_completed is only updated after user approval
      for (const agentName of Object.keys(agentDetails)) {
        const agentProgress = agentDetails[agentName];
        if (agentProgress && agentProgress.status === 'completed' && agentProgress.review_status === 'awaiting_review') {
          agentAwaitingReview = agentName;
          break; // Take the first one found (should only be one at a time)
        }
      }

      logger.debug('🔍 RESTORATION: Review status check:', {
        agentDetails: Object.keys(agentDetails).map(name => ({
          name,
          review_status: agentDetails[name]?.review_status
        })),
        agentAwaitingReview,
        workflowStatus: workflowStatus.status,
        workflowPhase: workflowStatus.phase,
        currentAgent: workflowStatus.current_agent
      });

      // Determine the step based on workflow status and review_status
      // Priority 1: Check if workflow is PAUSED (after revision or waiting for user approval)
      if (workflowStatus.status === 'paused' || workflowStatus.phase === 'paused') {
        // Workflow is paused - stay on the current agent's step for user review
        const pausedAgent = workflowStatus.current_agent || agentAwaitingReview;
        if (pausedAgent) {
          restorationData.currentStep = getStepForAgent(pausedAgent) || 1;
          restorationData.needsReview = true;
          restorationData.agentAwaitingReview = pausedAgent;
          // Include the paused agent in completedAgents so the restoration loop processes its data
          if (!restorationData.completedAgents.includes(pausedAgent)) {
            restorationData.completedAgents = [...restorationData.completedAgents, pausedAgent];
          }
          logger.debug('⏸️ RESTORATION: Workflow is PAUSED - staying on step', restorationData.currentStep, 'for agent', pausedAgent);
        } else {
          // Paused but no current agent - use highest completed step
          restorationData.currentStep = determineCurrentStep(workflowStatus.agents_completed) - 1;
          restorationData.needsReview = true;
        }
      } else if (agentAwaitingReview) {
        // Priority 2: An agent is explicitly awaiting user review
        restorationData.currentStep = getStepForAgent(agentAwaitingReview) || 1;
        restorationData.needsReview = true;
        restorationData.agentAwaitingReview = agentAwaitingReview;
        // Include the awaiting-review agent in completedAgents so the restoration loop processes its data
        if (!restorationData.completedAgents.includes(agentAwaitingReview)) {
          restorationData.completedAgents = [...restorationData.completedAgents, agentAwaitingReview];
        }
      } else {
        // Priority 3: All completed agents are approved - move to next step
        restorationData.currentStep = determineCurrentStep(workflowStatus.agents_completed);
        restorationData.needsReview = false;
        restorationData.agentAwaitingReview = null;
      }

      logger.debug('✅ RESTORATION: Updated restorationData with workflow status:', {
        completedAgents: restorationData.completedAgents,
        currentAgent: restorationData.currentAgent,
        currentStep: restorationData.currentStep,
        needsReview: restorationData.needsReview,
        agentAwaitingReview: restorationData.agentAwaitingReview
      });
    } else {
      logger.debug('⚠️ RESTORATION: No API override data available');
    }

    // Update backend session in state — Story 4.2: dispatched via
    // WorkflowSessionContext now that the Session slice owns this state.
    workflowSession.updateBackendSession({
      sessionId: restorationData.sessionId,
      threadId: restorationData.threadId,
      fileId: restorationData.fileId,
      tenantId: restorationData.tenantId
    });

    // Restore session data and UI state for all completed agents
    logger.debug('🔍 RESTORATION DEBUG - Checking completed agents:', {
      hasCompletedAgents: !!restorationData.completedAgents,
      completedAgentsLength: restorationData.completedAgents?.length,
      completedAgents: restorationData.completedAgents
    });

    if (restorationData.completedAgents && restorationData.completedAgents.length > 0) {
      logger.debug('✅ RESTORATION: Restoring data for completed agents:', restorationData.completedAgents);

      // Initialize sessionData with backend status mapped to session_status
      // Backend uses "status" field, frontend reads "session_status"
      const mappedStatus = session.status || session.session_status || 'IN_PROGRESS';
      logger.debug('🔍 RESTORATION: Mapping backend status to session_status:', {
        backendStatus: session.status,
        backendSessionStatus: session.session_status,
        mappedStatus: mappedStatus
      });

      dispatch({
        type: ACTIONS.UPDATE_SESSION_DATA,
        payload: {
          session_status: mappedStatus,
          completed_agents: restorationData.completedAgents,
          current_agent: restorationData.currentAgent,
          agent_awaiting_review: restorationData.agentAwaitingReview || null,
          // Hierarchical TARA fields
          analysis_mode: restorationData.analysis_mode || 'individual',
          tara_level: restorationData.tara_level || 'unassigned',
          parent_sessions: restorationData.parent_sessions || []
        }
      });

      // Clear existing messages to avoid duplicates
      workflowMessages.clearMessages();

      // Add welcome message matching normal workflow
      dispatch({
        type: ACTIONS.ADD_MESSAGE,
        payload: {
          id: `welcome_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
          type: 'assistant',
          content: `Welcome to Athena TARA Assistant! 🛡️

I'm here to guide you through a comprehensive Threat Analysis and Risk Assessment following ISO/SAE 21434 standards.

**We'll go through 9 steps together:**

**Step 1: Item Definition**
Define the item/component under analysis, its boundaries, and preliminary architecture

**Step 2: Asset Identification**
Identify and categorize cybersecurity assets and their properties

**Step 3: Damage Scenarios**
Analyze potential damage scenarios and their impact

**Step 4: Threat Analysis**
Identify potential threat scenarios using STRIDE methodology

**Step 5: Attack Path Feasibility**
Map out attack paths and assess feasibility

**Step 6: Risk Assessment**
Calculate risk values and determine treatment decisions

**Step 7: Cybersecurity Goals**
Generate goals for Reduce treatments and claims for Share/Retain treatments

**Step 8: Cybersecurity Concept**
Generate controls and cybersecurity requirements

**Step 9: Final Report**
Generate comprehensive analysis summary`,
          timestamp: new Date().toISOString()
        }
      });

      // Restoration info message removed - silent restoration

      // Process each completed agent in order
      for (const agentName of restorationData.completedAgents) {
        // Check if this agent is awaiting review
        // If so, restore data but skip message creation (message created when user approves)
        const isAwaitingReview = agentName === restorationData.agentAwaitingReview;
        if (isAwaitingReview) {
          logger.debug(`⏳ RESTORATION: ${agentName} is awaiting review - will restore data but skip message`);
        }

        try {
          const agentResult = await getAgentResult(backendServiceRef.current.threadId, agentName);
          logger.debug(`Fetched result for ${agentName}:`, agentResult);

          if (agentResult) {
            // Handle different response structures
            let resultData = agentResult.result || agentResult;

            // Handle nested result structure from API
            // API returns: { result: { timestamp, agent_name, extraction_method, result: {...actual data} } }
            if (resultData.result && typeof resultData.result === 'object') {
              logger.debug('Found nested result structure, extracting inner result');
              resultData = resultData.result;
            }

            if (resultData.data) {
              resultData = resultData.data;
            }

            logger.debug('Final extracted resultData:', resultData);

            // Get step number for this agent
            // eslint-disable-next-line no-unused-vars
            const stepNumber = getStepForAgent(agentName);

            // Restore data and create completion message based on agent type
            switch (agentName) {
              case 'analysis_agent':
                // Detect format: display-formatted (has core_information) vs raw (has item_name)
                // Display format comes from CompleteItemDefinition edits
                // Raw format comes from original analysis_agent output
                const isDisplayFormat = resultData.core_information !== undefined;

                let itemName, itemDescription, itemPurpose, itemScope, components, interfaces, dataFlows, confidence;

                if (isDisplayFormat) {
                  // Extract from display-formatted structure (after CompleteItemDefinition edits)
                  logger.debug('Extracting item definition from DISPLAY format');
                  const core = resultData.core_information || {};
                  const arch = resultData.system_architecture || {};
                  const meta = resultData.metadata || {};
                  const ifaces = resultData.interfaces_and_connectivity || {};

                  itemName = core.item_name || 'System Under Analysis';
                  itemDescription = core.description || '';
                  itemPurpose = core.purpose || '';
                  itemScope = core.scope?.definition || '';
                  components = arch.components?.list || [];
                  // Interfaces can be at interfaces_and_connectivity.network_interfaces (after edit)
                  // or at system_architecture.interfaces.list (from presenter)
                  interfaces = ifaces.network_interfaces || arch.interfaces?.list || [];
                  dataFlows = arch.data_flows?.basic || [];
                  confidence = meta.overall_confidence || 0;
                } else {
                  // Extract from raw format (original analysis_agent output)
                  logger.debug('Extracting item definition from RAW format');
                  itemName = resultData.item_name || resultData.itemName || resultData.name || 'System Under Analysis';
                  itemDescription = resultData.item_description || resultData.itemDescription || resultData.description || '';
                  itemPurpose = resultData.item_purpose || resultData.itemPurpose || '';
                  itemScope = resultData.item_scope || resultData.itemScope || '';
                  components = resultData.components || [];
                  interfaces = resultData.interfaces || [];
                  dataFlows = resultData.data_flows || resultData.dataFlows || [];
                  confidence = resultData.confidence || resultData.confidence_score || 0;
                }

                // Create the item definition payload
                const itemDefinitionPayload = {
                  itemName: itemName,
                  itemDescription: itemDescription,
                  itemPurpose: itemPurpose,
                  itemScope: itemScope,
                  components: components,
                  interfaces: interfaces,
                  dataFlows: dataFlows,
                  confidence: confidence
                };

                // Restore item definition data
                dispatch({
                  type: ACTIONS.SET_ITEM_DEFINITION,
                  payload: itemDefinitionPayload
                });

                // Add Step 1 component message (only if approved, not awaiting review)
                if (!isAwaitingReview) {
                  dispatch({
                    type: ACTIONS.ADD_MESSAGE,
                    payload: {
                      id: `step1_result_${Date.now()}_1`,
                      type: 'step_result',
                      stepNumber: 1,
                      stepName: 'Item Definition',
                      component: 'Step1ItemDefinition',
                      data: itemDefinitionPayload,
                      showComponent: true,
                      timestamp: new Date().toISOString(),
                      threadId: restorationData.threadId,
                      content: `✅ **Step 1: Item Definition - Complete**

**System:** ${itemName}
**Description:** ${itemDescription}`,
                      stepCompleted: 1
                    }
                  });
                  logger.debug('Restored Step 1 with full component data:', itemName);
                }
                break;

              case 'asset_identification_agent':

                // Use the extractStep2Data function to properly preserve all fields including hierarchical metadata
                const assetsData = extractStep2Data({ result: resultData });
                logger.debug('Extracted assets data:', assetsData?.length);

                // Restore assets data
                dispatch({
                  type: ACTIONS.SET_ASSETS,
                  payload: assetsData
                });

                // Add Step 2 component message (only if approved, not awaiting review)
                if (!isAwaitingReview) {
                  dispatch({
                    type: ACTIONS.ADD_MESSAGE,
                    payload: {
                      id: `step2_result_${Date.now()}_2`,
                      type: 'step_result',
                      stepNumber: 2,
                      stepName: 'Asset Identification',
                      component: 'Step2AssetIdentification',
                      data: { assets: assetsData },
                      showComponent: true,
                      timestamp: new Date().toISOString(),
                      content: `✅ **Step 2: Asset Identification - Complete**

**Assets Identified:** ${assetsData.length} cybersecurity-relevant assets`,
                      stepCompleted: 2
                    }
                  });
                  logger.debug('Restored Step 2 with full component data:', assetsData.length, 'assets');
                }
                break;

              case 'threat_analysis_agent':

                // Use the extractStep4Data function to properly preserve all fields including hierarchical metadata
                const threatsData = extractStep4Data({ result: resultData });
                logger.debug('Extracted threats data:', threatsData?.length);

                // Restore threats data
                dispatch({
                  type: ACTIONS.SET_THREATS,
                  payload: threatsData
                });

                // Add Step 4 component message (only if approved, not awaiting review)
                if (!isAwaitingReview) {
                  dispatch({
                    type: ACTIONS.ADD_MESSAGE,
                    payload: {
                      id: `step4_result_${Date.now()}_4`,
                      type: 'step_result',
                      stepNumber: 4,
                      stepName: 'Threat Identification',
                      component: 'Step4ThreatIdentification',
                      data: { threats: threatsData },
                      showComponent: true,
                      timestamp: new Date().toISOString(),
                      content: `✅ **Step 4: Threat Identification - Complete**

**Threats Identified:** ${threatsData.length} potential threat scenarios`,
                      stepCompleted: 4
                    }
                  });
                  logger.debug('Restored Step 4 with full component data:', threatsData.length, 'threats');
                }
                break;

              // Add cases for other agents as needed (ISO 21434 order: damage scenarios before threats)
              case 'damage_scenario_agent':

                // Use the extractStep3Data function to properly map the damage scenarios
                const extractedDamageScenarios = extractStep3Data({ result: resultData });
                logger.debug('Extracted damage scenarios:', extractedDamageScenarios?.length);

                if (extractedDamageScenarios && extractedDamageScenarios.length > 0) {
                  dispatch({ type: ACTIONS.SET_DAMAGE_SCENARIOS, payload: extractedDamageScenarios });

                  // Add Step 3 component message (only if approved, not awaiting review)
                  if (!isAwaitingReview) {
                    dispatch({
                      type: ACTIONS.ADD_MESSAGE,
                      payload: {
                        id: `step3_result_${Date.now()}_3`,
                        type: 'step_result',
                        stepNumber: 3,
                        stepName: 'Damage Scenarios',
                        component: 'Step3DamageScenarios',
                        data: { damageScenarios: extractedDamageScenarios },
                        showComponent: true,
                        timestamp: new Date().toISOString(),
                        content: `✅ **Step 3: Damage Scenarios - Complete**

**Scenarios Analyzed:** ${extractedDamageScenarios.length} damage scenarios

Potential damage scenarios have been identified and assessed.`,
                        stepCompleted: 3
                      }
                    });
                    logger.debug('Restored Step 3 with full component data:', extractedDamageScenarios.length, 'damage scenarios');
                  }
                } else {
                  logger.warn('No damage scenarios found in restoration data');
                }
                break;

              case 'attack_path_feasibility_agent':

                // Use the extractStep5Data function to properly preserve all fields including hierarchical metadata
                const attackPathsArray = extractStep5Data({ result: resultData }) || [];
                logger.debug('Extracted attack paths:', attackPathsArray?.length);

                if (attackPathsArray.length > 0) {
                  dispatch({ type: ACTIONS.SET_ATTACK_PATHS, payload: attackPathsArray });

                  // Add Step 5 component message (only if approved, not awaiting review)
                  if (!isAwaitingReview) {
                    dispatch({
                      type: ACTIONS.ADD_MESSAGE,
                      payload: {
                        id: `step5_result_${Date.now()}`,
                        type: 'step_result',
                        stepNumber: 5,
                        stepName: 'Attack Path Analysis',
                        component: 'Step5AttackPaths',
                        data: { attackPaths: attackPathsArray },
                        showComponent: true,
                        content: `✅ **Step 5: Attack Path Analysis - Complete**

**Attack Paths:** ${attackPathsArray?.length || 0} potential attack paths

Attack feasibility analysis has been completed.`,
                        timestamp: new Date().toISOString(),
                        stepCompleted: 5
                      }
                    });
                    logger.debug('Restored Step 5 with full component data:', attackPathsArray?.length || 0, 'attack paths');
                  }
                } else {
                  logger.warn('No attack paths found in restoration data');
                }
                break;

              case 'risk_assessment_agent':

                // Extract risks using the proper extraction function (returns array directly)
                const risksData = extractStep6Data({ result: resultData }) || [];
                logger.debug('Extracted risks data:', risksData?.length);

                dispatch({ type: ACTIONS.SET_RISKS, payload: risksData });

                // Add Step 6 component message (only if approved, not awaiting review)
                if (!isAwaitingReview) {
                  dispatch({
                    type: ACTIONS.ADD_MESSAGE,
                    payload: {
                      id: `step6_result_${Date.now()}`,
                      type: 'step_result',
                      stepNumber: 6,
                      stepName: 'Risk Assessment',
                      component: 'Step6RiskAssessment',
                      data: { riskAssessments: risksData, risks: risksData },
                      showComponent: true,
                      content: `✅ **Step 6: Risk Assessment - Complete**

**Risks Assessed:** ${risksData.length} risk scenarios

Risk values have been calculated based on impact and likelihood.`,
                      timestamp: new Date().toISOString(),
                      stepCompleted: 6
                    }
                  });
                  logger.debug('Restored Step 6 with full component data:', risksData.length, 'risks');
                }
                break;

              case 'cybersecurity_goals_agent':
                // Agent is in completedAgents, so it has finished - always restore its data

                // Extract goals and claims using step 7 extractor (with fallback like risk_assessment)
                const step7Data = extractStep7Data ? extractStep7Data({ result: resultData }) : null;
                const goals = step7Data?.goals ||
                             resultData.cybersecurity_goals ||
                             resultData.goals || [];
                const claims = step7Data?.claims ||
                              resultData.cybersecurity_claims ||
                              resultData.claims || [];

                dispatch({ type: ACTIONS.SET_CYBERSECURITY_GOALS, payload: goals });
                dispatch({ type: ACTIONS.SET_CYBERSECURITY_CLAIMS, payload: claims });

                // Add Step 7 component message (only if approved, not awaiting review)
                if (!isAwaitingReview) {
                  dispatch({
                    type: ACTIONS.ADD_MESSAGE,
                    payload: {
                      id: `step7_result_${Date.now()}`,
                      type: 'step_result',
                      stepNumber: 7,
                      stepName: 'Cybersecurity Goals & Claims',
                      component: 'Step7CybersecurityGoals',
                      data: { goals, claims },
                      showComponent: true,
                      timestamp: new Date().toISOString(),
                      content: `✅ **Step 7: Cybersecurity Goals & Claims - Complete**

**Goals:** ${goals.length} cybersecurity goals
**Claims:** ${claims.length} cybersecurity claims`,
                      stepCompleted: 7
                    }
                  });
                  logger.debug('Restored Step 7 with goals:', goals.length, 'and claims:', claims.length);
                }
                break;

              case 'cybersecurity_concept_agent':
                // Extract controls and requirements using step 8 extractor
                const step8Data = extractStep8Data ? extractStep8Data({ result: resultData }) : null;
                const controls = step8Data?.controls ||
                               resultData.cybersecurity_controls ||
                               resultData.controls || [];
                const step8Requirements = step8Data?.requirements ||
                                    resultData.cybersecurity_requirements ||
                                    resultData.requirements || [];

                // Store BOTH controls and requirements in sessionData
                dispatch({ type: ACTIONS.SET_CYBERSECURITY_CONTROLS, payload: controls });
                dispatch({
                  type: ACTIONS.UPDATE_SESSION_DATA,
                  payload: { cybersecurityRequirements: step8Requirements }
                });
                // Also store in legacy requirements field for backward compatibility
                dispatch({ type: ACTIONS.SET_REQUIREMENTS, payload: step8Requirements });

                // Always add Step 8 message so user can see the results
                // Review panel will show on top if awaiting review
                dispatch({
                  type: ACTIONS.ADD_MESSAGE,
                  payload: {
                    id: `step8_result_${Date.now()}`,
                    type: 'step_result',
                    stepNumber: 8,
                    stepName: 'Cybersecurity Concept',
                    component: 'Step8CybersecurityConcept',
                    data: { controls, requirements: step8Requirements },
                    showComponent: true,
                    timestamp: new Date().toISOString(),
                    content: isAwaitingReview
                      ? `🔍 **Step 8: Cybersecurity Concept - Awaiting Review**

**Controls:** ${controls.length} cybersecurity controls
**Requirements:** ${step8Requirements.length} cybersecurity requirements`
                      : `✅ **Step 8: Cybersecurity Concept - Complete**

**Controls:** ${controls.length} cybersecurity controls
**Requirements:** ${step8Requirements.length} cybersecurity requirements`,
                    stepCompleted: 8
                  }
                });
                logger.debug('Restored Step 8 with controls:', controls.length, 'and requirements:', step8Requirements.length);
                break;

              case 'final_report_agent':
                const report = resultData.report || resultData.final_report || null;
                if (report) {
                  dispatch({ type: ACTIONS.SET_REPORT, payload: report });
                }

                // Add Step 9 component message (only if approved, not awaiting review)
                if (!isAwaitingReview) {
                  dispatch({
                    type: ACTIONS.ADD_MESSAGE,
                    payload: {
                      id: `step9_result_${Date.now()}`,
                      type: 'step_result',
                      stepNumber: 9,
                      stepName: 'Final Report',
                      component: 'Step9FinalReport',
                      data: { report: report },
                      showComponent: true,
                      showFinalReport: true,
                      content: `## 🎉 TARA Analysis Complete!

Your comprehensive Threat Analysis and Risk Assessment has been successfully completed. Below is the final report summarizing all findings and recommendations.

The analysis covered:
- Item definition and system boundaries
- ${restorationData.sessionData?.assets?.length || 0} identified assets
- ${restorationData.sessionData?.threats?.length || 0} potential threats
- ${restorationData.sessionData?.damageScenarios?.length || 0} damage scenarios
- ${restorationData.sessionData?.attackPaths?.length || 0} attack paths
- ${restorationData.sessionData?.risks?.length || 0} risk assessments
- ${restorationData.sessionData?.cybersecurityGoals?.length || 0} cybersecurity goals
- ${restorationData.sessionData?.cybersecurityClaims?.length || 0} cybersecurity claims`,
                      timestamp: new Date().toISOString(),
                      stepCompleted: 9
                    }
                  });
                  logger.debug('Restored Step 9 with full component data and report');
                }
                break;

              case 'control_implementation':
                // Step 10: Residual TARA - Control Implementation
                // This is a separate workflow phase, fetch residual progress data
                try {
                  const residualProgress = await getResidualProgress(restorationData.sessionId);

                  if (residualProgress && residualProgress.controls) {
                    // Store residual TARA data in sessionData
                    dispatch({
                      type: ACTIONS.UPDATE_SESSION_DATA,
                      payload: {
                        residualTARA: residualProgress,
                        residualControls: residualProgress.controls
                      }
                    });

                    // Do NOT add Step 10 component message - it's rendered as current interactive step via orchestrator
                    // Step 10 should only appear once as the active step, not as a static component in chat history
                    logger.debug('Step 10 data restored in agent loop - will render as current interactive step');
                  }
                } catch (error) {
                  // Residual TARA might not be initialized yet, that's okay
                  logger.debug('Residual TARA not initialized or not found for session');
                }
                break;

              default:
                logger.warn(`Unknown agent type for restoration: ${agentName}`);
            }
          }
        } catch (error) {
          logger.error(`Failed to fetch result for agent ${agentName}:`, error);
        }
      }

      // After restoring all completed agents, check if workflow is COMPLETED
      // Only show Step 9 if user has already approved Step 8
      // Backend marks workflow "COMPLETED" when last agent finishes, but we need to wait for user approval
      // Check: workflow is COMPLETED AND no agent is awaiting review (user approved all steps)
      const isWorkflowCompleted = workflowStatus?.status?.toUpperCase() === 'COMPLETED' ||
                                   session.session_status?.toUpperCase() === 'COMPLETED' ||
                                   session.status?.toUpperCase() === 'COMPLETED';

      if (isWorkflowCompleted && !restorationData.needsReview) {
        logger.debug('✅ RESTORATION: Workflow is COMPLETED, adding Step 9 (Final Report) message');

        // Update session data to reflect COMPLETED status
        dispatch({
          type: ACTIONS.UPDATE_SESSION_DATA,
          payload: {
            session_status: 'COMPLETED',
            completed_agents: restorationData.completedAgents
          }
        });

        logger.debug('🔍 RESTORATION: Updated session_status to COMPLETED for editing all steps');

        dispatch({
          type: ACTIONS.ADD_MESSAGE,
          payload: {
            id: `step9_result_${Date.now()}`,
            type: 'step_result',
            stepNumber: 9,
            stepName: 'Final Report',
            component: 'Step9FinalReport',
            data: restorationData.sessionData || {},
            showComponent: true,
            showFinalReport: true,
            content: `## 🎉 TARA Analysis Complete!

Your comprehensive Threat Analysis and Risk Assessment has been successfully completed. Below is the final report summarizing all findings and recommendations.

The analysis covered:
- Item definition and system boundaries
- ${restorationData.sessionData?.assets?.length || 0} identified assets
- ${restorationData.sessionData?.threats?.length || 0} potential threats
- ${restorationData.sessionData?.damageScenarios?.length || 0} damage scenarios
- ${restorationData.sessionData?.attackPaths?.length || 0} attack paths
- ${restorationData.sessionData?.risks?.length || 0} risk assessments
- ${restorationData.sessionData?.cybersecurityGoals?.length || 0} cybersecurity goals
- ${restorationData.sessionData?.cybersecurityClaims?.length || 0} cybersecurity claims`,
            timestamp: new Date().toISOString(),
            stepCompleted: 9
          }
        });

        // Mark Step 9 as current step and mark all steps as completed
        stepNavigation.navigateToStep(9, true);
        stepNavigation.markStepCompleted(9);
        stepNavigation.updateStepValidation(9, {
          approved: true,
          validatedAt: new Date().toISOString(),
          feedback: 'Analysis complete'
        });
        dispatch({ type: ACTIONS.SET_SHOW_STEP_COMPONENT, payload: false });
      }
    } else {
      logger.debug('⚠️ RESTORATION: No completed agents found, adding basic restoration message');

      // Clear existing messages to avoid duplicates
      workflowMessages.clearMessages();

      // Add welcome message matching normal workflow
      dispatch({
        type: ACTIONS.ADD_MESSAGE,
        payload: {
          id: `welcome_basic_${Date.now()}`,
          type: 'assistant',
          content: `Welcome to Athena TARA Assistant! 🛡️

I'm here to guide you through a comprehensive Threat Analysis and Risk Assessment following ISO/SAE 21434 standards.

**We'll go through 9 steps together:**

**Step 1: Item Definition**
Define the item/component under analysis, its boundaries, and preliminary architecture

**Step 2: Asset Identification**
Identify and categorize cybersecurity assets and their properties

**Step 3: Damage Scenarios**
Analyze potential damage scenarios and their impact

**Step 4: Threat Analysis**
Identify potential threat scenarios using STRIDE methodology

**Step 5: Attack Path Feasibility**
Map out attack paths and assess feasibility

**Step 6: Risk Assessment**
Calculate risk values and determine treatment decisions

**Step 7: Cybersecurity Goals**
Generate goals for Reduce treatments and claims for Share/Retain treatments

**Step 8: Cybersecurity Concept**
Generate controls and cybersecurity requirements

**Step 9: Final Report**
Generate comprehensive analysis summary

**Restoring your session: ${restorationData.analysisName}**
Current Step: Step ${restorationData.currentStep}`,
          timestamp: new Date().toISOString()
        }
      });
    }


    // Workflow progress summary message removed - silent restoration

    // Update workflow state - if needs review, set to REVIEWING state
    if (restorationData.needsReview) {
      // Set to reviewing state for the completed step
      dispatch({
        type: ACTIONS.SET_WORKFLOW_STATE,
        payload: {
          state: WORKFLOW_STATES.REVIEWING,
          context: {
            step: restorationData.currentStep,
            agent: restorationData.completedAgents[restorationData.completedAgents.length - 1],
            isRestored: true,
            restorationTime: new Date().toISOString()
          }
        }
      });

      // Show the review panel for the last completed step
      setTimeout(() => {
        // Get the data for the last completed step
        const lastCompletedAgent = restorationData.completedAgents[restorationData.completedAgents.length - 1];
        const stepNum = getStepForAgent(lastCompletedAgent);
        // Story 4.4: sessionData sourced from WorkflowResultsContext.
        const stepData = workflowResults.sessionData[getStepDataKey(stepNum)];

        // Import createReviewData dynamically to avoid circular dependency
        import('../../../config/reviewQuestionsConfig').then(({ createReviewData }) => {
          // Create review data exactly like the normal flow does
          const reviewData = createReviewData(stepNum, stepData);
          // Add agent name for permission checks
          reviewData.agentName = lastCompletedAgent;

          // Show review panel with proper review data
          showReviewPanel(true, reviewData);
        });
      }, 500);
    } else {
      // Continue with processing state for next step
      dispatch({
        type: ACTIONS.SET_WORKFLOW_STATE,
        payload: {
          state: WORKFLOW_STATES.PROCESSING,
          context: {
            step: restorationData.currentStep,
            agent: restorationData.currentAgent,
            isRestored: true,
            restorationTime: new Date().toISOString()
          }
        }
      });
    }

    // Update step navigation with completed steps for progress bar
    const completedStepNumbers = restorationData.completedAgents.map(agent => getStepForAgent(agent)).filter(step => step > 0);
    logger.debug('🔄 RESTORATION: Updating step navigation with completed steps:', completedStepNumbers);

    // Mark all completed steps in step navigation
    completedStepNumbers.forEach(stepNum => {
      stepNavigation.markStepCompleted(stepNum);
      stepNavigation.updateStepValidation(stepNum, {
        approved: true,
        validatedAt: new Date().toISOString(),
        feedback: 'Restored from session'
      });
    });

    // Only mark step 9 as complete if workflow status is COMPLETED
    // (meaning user has already approved Step 8)
    const isWorkflowCompleted = workflowStatus?.status?.toUpperCase() === 'COMPLETED' ||
                                 session.session_status?.toUpperCase() === 'COMPLETED' ||
                                 session.status?.toUpperCase() === 'COMPLETED';

    // Check if residual TARA exists and should navigate to Step 10 or Step 11
    // eslint-disable-next-line no-unused-vars
    let shouldNavigateToStep10 = false;
    if (isWorkflowCompleted) {
      stepNavigation.markStepCompleted(9);
      stepNavigation.updateStepValidation(9, {
        approved: true,
        validatedAt: new Date().toISOString(),
        feedback: 'Analysis complete'
      });

      // Check for residual TARA data
      try {
        const residualProgress = await getResidualProgress(restorationData.sessionId);
        if (residualProgress && residualProgress.controls && residualProgress.controls.length > 0) {
          const phase = residualProgress.current_phase;

          // All phases now render in Step 10
          logger.debug('✅ RESTORATION: Phase is', phase, '- navigating to Step 10');
          restorationData.currentStep = 10;
          shouldNavigateToStep10 = true;

          // Only mark Step 10 as completed if phase is 'completed'
          if (phase === 'completed') {
            stepNavigation.markStepCompleted(10);
            stepNavigation.updateStepValidation(10, {
              approved: true,
              validatedAt: new Date().toISOString(),
              feedback: 'Residual TARA workflow completed'
            });
          }

          // Add Step 10 data to session
          dispatch({
            type: ACTIONS.UPDATE_SESSION_DATA,
            payload: {
              residualTARA: residualProgress
            }
          });

          // Do NOT add Step 10 message - it's rendered as current interactive step via orchestrator
          // Step 10 should only appear once as the active step, not as a static component in chat history
          logger.debug('Step 10 restored - will render as current interactive step, phase:', phase);
        } else {
          // No residual TARA - check for impact analysis
          try {
            const impactData = await getThreatChain(restorationData.sessionId);
            if (impactData?.some(t => t.evaluation)) {
              logger.debug('✅ RESTORATION: Impact analysis found - navigating to Step 10');
              restorationData.currentStep = 10;
              shouldNavigateToStep10 = true;
            } else {
              restorationData.currentStep = 9;
            }
          } catch (impactError) {
            logger.debug('No impact analysis found:', impactError);
            restorationData.currentStep = 9;
          }
        }
      } catch (error) {
        // Residual TARA not initialized - check for impact analysis
        logger.debug('Residual TARA not initialized:', error);
        try {
          const impactData = await getThreatChain(restorationData.sessionId);
          if (impactData?.some(t => t.evaluation)) {
            logger.debug('✅ RESTORATION: Impact analysis found - navigating to Step 10');
            restorationData.currentStep = 10;
            shouldNavigateToStep10 = true;
          } else {
            restorationData.currentStep = 9;
          }
        } catch (impactError) {
          restorationData.currentStep = 9;
        }
      }
    }

    // Update current step in navigation
    stepNavigation.navigateToStep(restorationData.currentStep, true);

    // Don't add restoration message here - let orchestrator handle it
    // to avoid duplicates

    // Set flag to indicate restoration
    dispatch({
      type: ACTIONS.UPDATE_SESSION_DATA,
      payload: {
        isRestored: true,
        restorationData
      }
    });

    // Only show step component if we're not in review mode
    if (!restorationData.needsReview) {
      dispatch({ type: ACTIONS.SET_SHOW_STEP_COMPONENT, payload: true });
    }

    logger.debug('🎉 ENHANCED RESTORATION COMPLETE:', restorationData);
    return restorationData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowSession, stepNavigation]);

  return restoreInProgressSession;
}
